import { Button, useNavigate } from '@forgeglobal/design-framework';
import { AssetValuationDetails } from '@tcs/client-portal-api/src/types/AssetValuationDetails';
import { Status } from '@tcs/common/types';
import formatDate from '@tcs/common/utils/formatDate';
import addDays from 'date-fns/addDays';
import differenceInDays from 'date-fns/differenceInDays';
import { useCallback, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { NotificationContext } from '../providers/NotificationProvider';
import useAccounts from './useAccounts';
import { useValuationDetailsMultiple } from './useValuationDetails';
import { ValuationModel } from '@tcs/client-portal-api/platform_generated';

const useValuationsNotifications = () => {
  const { accountNumber } = useParams();

  const { addNotifications } = useContext(NotificationContext);

  const navigate = useNavigate();

  const { data: accounts = [] } = useAccounts({ enabled: false });

  const { data: valuations, loading } = useValuationDetailsMultiple(
    accounts?.map(({ accountNumber }) => accountNumber)
  );

  const getValuationDueDate = useCallback((date: string) => {
    if (!date) return new Date().toDateString();
    return formatDate(addDays(new Date(date), 365).toDateString());
  }, []);

  const getValuationStatus = useCallback(
    (date: string): Status => {
      if (!date) return 'error';
      const valuationDueDate = getValuationDueDate(date);
      const daysToDueDate =
        differenceInDays(new Date(valuationDueDate), new Date()) + 1;
      if (daysToDueDate <= 0) return 'error';
      if (daysToDueDate <= 60) return 'warning';
      return 'info';
    },
    [getValuationDueDate]
  );

  const filterValuationDetails = useCallback(
    (assetValuationDetails: AssetValuationDetails[] = []) => {
      return assetValuationDetails
        .filter((item) => item.isUpdateable)
        .filter(
          (item) => item.status !== ValuationModel.ValuationStatus.PENDING
        )
        .filter((item) => item.status !== ValuationModel.ValuationStatus.NEW)
        .filter((item) => {
          return ['error', 'warning'].includes(
            getValuationStatus(item.valuationDate)
          );
        });
    },
    [getValuationStatus]
  );

  useEffect(() => {
    if (loading) {
      return;
    }

    const notifications = valuations?.reduce((accumulator, valuation) => {
      const assetsPastDueDate = filterValuationDetails(
        valuation.assetValuationDetails
      );

      return [
        ...accumulator,
        ...assetsPastDueDate.map((asset) => ({
          text: `REMINDER: You have an asset valuation due on ${getValuationDueDate(
            asset.valuationDate
          )}. Please submit new values in a timely manner`,
          status: getValuationStatus(asset.valuationDate),
          action: (
            <Button
              onClick={() =>
                navigate(
                  !accountNumber
                    ? `/accounts/all/asset_valuation?dialog=valuation-accountNumber-${valuation.accountNumber}-asset-${asset.id}`
                    : `/accounts/${valuation.accountNumber}/asset_valuation?dialog=valuation-accountNumber-${valuation.accountNumber}-asset-${asset.id}`
                )
              }
              variant="text"
              color="primary"
              data-test-id="valuation-due-banner-button">
              Update asset value
            </Button>
          ),
          type: 'valuation',
          id: valuation.accountNumber,
        })),
      ];
    }, []);

    if (notifications && notifications.length > 0) {
      addNotifications(notifications, true, true);
    }
  }, [loading, valuations, accountNumber]);
};

export default useValuationsNotifications;
